import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { LanguageCode } from '@/i18n';

export interface TermsDocument {
  id: string;
  title_translations: Partial<Record<LanguageCode, string>>;
  description_translations?: Partial<Record<LanguageCode, string>>;
  file_path: string;
  file_name: string;
  languages: LanguageCode[]; // tom array = visas på alla språk
  sort_order: number;
  updated_at?: string; // ISO-tid när dokumentet senast ändrades
}

export interface TermsDocumentsValue {
  documents: TermsDocument[];
}

const SETTING_KEY = 'terms_documents';

/** Publik hook — hämtar dokumentlistan filtrerad på språk. */
export function useTermsDocuments(lang: LanguageCode) {
  return useQuery({
    queryKey: ['terms-documents', lang],
    queryFn: async (): Promise<TermsDocument[]> => {
      const { data, error } = await (supabase as any)
        .from('site_settings')
        .select('value')
        .eq('key', SETTING_KEY)
        .maybeSingle();
      if (error) throw error;
      const value = (data?.value as TermsDocumentsValue | null) ?? { documents: [] };
      const docs = Array.isArray(value.documents) ? value.documents : [];
      return docs
        .filter((d) => !d.languages?.length || d.languages.includes(lang))
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Admin-hook — hämtar alla dokument oavsett språk. */
export function useAllTermsDocuments() {
  return useQuery({
    queryKey: ['terms-documents-all'],
    queryFn: async (): Promise<TermsDocument[]> => {
      const { data, error } = await (supabase as any)
        .from('site_settings')
        .select('value')
        .eq('key', SETTING_KEY)
        .maybeSingle();
      if (error) throw error;
      const value = (data?.value as TermsDocumentsValue | null) ?? { documents: [] };
      const docs = Array.isArray(value.documents) ? value.documents : [];
      return docs.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    },
  });
}

export { SETTING_KEY as TERMS_DOCUMENTS_KEY };
